<script lang="ts" setup>
import { computed } from 'vue';
import { AppTypography } from '~/components/base';
import type { Campaign } from '~/types/Candidates';
import { opensCampaignModal } from '~/utils/campaignLink';
import { campaignTypeLabel } from '~/utils/campaignTypeLabel';
import { $stripHtml } from '~/utils/helpers';

const { campaign } = defineProps<{
  campaign: Campaign;
}>();

const emit = defineEmits<{ open: [campaign: Campaign] }>();

const href = computed(() => `/campaigns/${campaign.id}`);

// Same rule as the listing's CampaignCard: desktop previews the campaign in a
// modal, mobile follows the link. A plain <a> because NuxtLink navigates before
// a `@click` listener can preventDefault.
function onClick(event: MouseEvent) {
  if (!opensCampaignModal(event)) return;

  event.preventDefault();
  emit('open', campaign);
}

const typeLabel = computed(() => campaignTypeLabel(campaign));

// Descriptions are stored as HTML on the backend; this clamped preview shows
// plain text, so strip the markup rather than rendering tags literally.
const plainDescription = computed(() => $stripHtml(campaign.description));
</script>

<template>
  <a
    :href="href"
    data-testid="campaign-mini-card"
    class="campaign-mini-card flex flex-col no-underline"
    @click="onClick"
  >
    <span class="campaign-mini-card__tag inline-flex w-fit items-center">
      <AppTypography type="caption-bold" element="span" class="uppercase">{{
        typeLabel
      }}</AppTypography>
    </span>

    <AppTypography type="body-bold" element="p" class="mt-3">
      {{ campaign.title }}
    </AppTypography>

    <AppTypography
      type="subheadline"
      element="p"
      class="campaign-mini-card__description text-color-tag mt-2"
    >
      {{ plainDescription }}
    </AppTypography>

    <span
      data-testid="campaign-learn-more"
      class="campaign-mini-card__learn-more mt-auto flex w-fit items-center gap-1.5 pt-4"
    >
      <AppTypography type="footnote-bold" element="span" class="underline">
        Learn More
      </AppTypography>
      <Icon name="solar:arrow-right-linear" class="shrink-0" />
    </span>
  </a>
</template>

<style scoped lang="scss">
.campaign-mini-card {
  height: 100%;
  padding: $spacing-lg;
  border: 1px solid $border-color;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;
  color: inherit;

  &__tag {
    padding: 2px $spacing-sm;
    border-radius: $radius-2xl;
    background-color: $complementary-color-shade-1;
    color: $text-color-primary;
  }

  &__description {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    line-height: 1.2rem;
  }

  &__learn-more {
    border: none;
    background: transparent;
    color: $text-color-brand;
    cursor: pointer;
  }
}
</style>
